import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../../Componentes/Overlay";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import Modal from "../../../Componentes/Utils/Modal";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const Title = styled.h3`
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
`;

const Field = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
`;

const FieldLabel = styled.p`
    margin: 15px auto 5px auto;
    font-weight: bold;
    font-size: 14px;
    color: #000;
`;

const ErrorMsg = styled.p`
    margin: 12px 0 0 0;
    font-size: 13px;
    color: #b91c1c;
    text-align: center;
`;

const BotoesWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const Botao = styled.button`
    padding: 9px 14px;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#2563eb")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: 0.2s all ease;

    &:disabled {
        background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#93c5fd")};
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#1d4ed8")};
    }
`;

const ModalAdicionarResponsavel = ({ conta, userIdsAtuais = [], onSaved }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [selecionados, setSelecionados] = useState([]);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(
                    "/Painel/API/Contabilidade/Responsaveis/Usuarios/index.php",
                    { params: { conta } }
                );
                if (!ativo) return;
                setUsuarios(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                if (ativo) notifyError("Erro ao carregar usuários elegíveis.");
            } finally {
                if (ativo) setLoading(false);
            }
        };

        carregar();

        return () => {
            ativo = false;
        };
    }, [api, conta, notifyError]);

    const usuariosOptions = useMemo(
        () => usuarios.map((u) => ({ value: u.id, label: u.usuario })),
        [usuarios]
    );

    const handleConfirm = useCallback(async () => {
        if (saving || selecionados.length === 0) return;

        setSaving(true);
        setErro(null);

        const userIds = [...new Set([...userIdsAtuais, ...selecionados])];

        try {
            const { data } = await api.post(
                "/Painel/API/Contabilidade/Responsaveis/Save/index.php",
                { conta, user_ids: userIds }
            );

            if (data?.success) {
                notifySuccess(data?.message || "Responsáveis adicionados.");
                await onSaved?.();
                closeModal();
                return;
            }

            setErro(data?.message || "Erro ao adicionar responsáveis.");
        } catch (err) {
            console.error(err);
            setErro(err?.response?.data?.message || "Erro ao adicionar responsáveis.");
        } finally {
            setSaving(false);
        }
    }, [
        api,
        conta,
        selecionados,
        userIdsAtuais,
        saving,
        notifySuccess,
        onSaved,
        closeModal,
    ]);

    return (
        <Overlay state>
            <Modal header close={closeModal} size="md">
                <Title>Adicionar responsáveis</Title>
                <Subtitle>
                    Selecione usuários com permissão de conciliar. Ao confirmar, a alteração é
                    salva imediatamente.
                </Subtitle>

                <Field>
                    <FieldLabel>USUÁRIOS</FieldLabel>
                    <MultiselectCheck
                        options={usuariosOptions}
                        selectedOptions={selecionados}
                        setSelectedOptions={setSelecionados}
                        placeholder={
                            loading
                                ? "Carregando..."
                                : usuariosOptions.length === 0
                                ? "Nenhum usuário elegível disponível"
                                : "USUÁRIOS..."
                        }
                    />
                </Field>

                {erro && <ErrorMsg>{erro}</ErrorMsg>}

                <BotoesWrapper>
                    <Botao $cancel type="button" onClick={closeModal} disabled={saving}>
                        CANCELAR
                    </Botao>
                    <Botao
                        type="button"
                        onClick={handleConfirm}
                        disabled={saving || loading || selecionados.length === 0}
                    >
                        {saving ? "SALVANDO…" : "CONFIRMAR"}
                    </Botao>
                </BotoesWrapper>
            </Modal>
        </Overlay>
    );
};

export default ModalAdicionarResponsavel;
